import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useAppConfig, usePatchAppConfig, type AppConfig } from "@/api/admin/app-config";
import { AdminErrorState } from "@/components/admin/AdminErrorState";

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const SEARCH: CSSProperties = {
  padding: "8px 12px",
  borderRadius: 999,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink-2)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
};
const ROW: CSSProperties = {
  padding: 12,
  border: "1px solid var(--d-line)",
  borderRadius: 12,
  background: "var(--d-ink-2)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const KEY_STYLE: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  color: "var(--d-fg)",
};
const DESC: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 12,
  color: "var(--d-fg-muted)",
};
const TEXTAREA: CSSProperties = {
  width: "100%",
  minHeight: 60,
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
};
const BTN: CSSProperties = {
  padding: "6px 12px",
  borderRadius: 999,
  border: "none",
  background: "var(--d-violet)",
  color: "#fff",
  cursor: "pointer",
};
const BTN_DISABLED: CSSProperties = { ...BTN, background: "var(--d-line)", cursor: "not-allowed" };

export function AppConfigPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading, isError, refetch } = useAppConfig();
  const patch = usePatchAppConfig();
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => (data ?? []).filter((r) => r.key.toLowerCase().includes(q.toLowerCase())),
    [data, q],
  );

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;

  return (
    <div style={PAGE}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>
        {t("app_config.title")}
      </h2>
      <input
        style={SEARCH}
        placeholder={t("app_config.search_placeholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {isLoading && <div>{t("common.loading")}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map((row) => (
          <AppConfigRow
            key={row.key}
            row={row}
            onSave={(value) => patch.mutate({ key: row.key, value }, {})}
          />
        ))}
      </div>
    </div>
  );
}

function AppConfigRow({
  row,
  onSave,
}: {
  row: AppConfig;
  onSave: (value: unknown) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const isBool = typeof row.value === "boolean";
  const [draft, setDraft] = useState(JSON.stringify(row.value, null, 2));
  const { parsed, valid } = useMemo(() => {
    try {
      return { parsed: JSON.parse(draft), valid: true };
    } catch {
      return { parsed: undefined, valid: false };
    }
  }, [draft]);
  const dirty = JSON.stringify(parsed) !== JSON.stringify(row.value);

  return (
    <div style={ROW}>
      <div style={KEY_STYLE}>{row.key}</div>
      {row.description && <div style={DESC}>{row.description}</div>}

      {isBool ? (
        <label>
          <input
            aria-label={row.key}
            type="checkbox"
            checked={row.value as boolean}
            onChange={(e) => onSave(e.target.checked)}
          />
        </label>
      ) : (
        <>
          <textarea
            aria-label={row.key}
            style={TEXTAREA}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {!valid && (
            <div style={{ fontSize: 12, color: "var(--d-ember)" }}>
              {t("app_config.json_invalid")}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              aria-label={`save ${row.key}`}
              style={valid && dirty ? BTN : BTN_DISABLED}
              disabled={!valid || !dirty}
              onClick={() => onSave(parsed)}
            >
              {t("common.save")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
